// ============================================================
// Datei: src/components/GuestRegistrationDesktop.jsx
//
// Zweck:
// - Formular zum Erfassen von Gästen
// - Wird von GuestRegistration.jsx für Desktop verwendet
// - Wird bei Kasse und Admin verwendet
//
// ÄNDERUNGEN:
// - Rollenauswahl zeigt jetzt exakt die Rollen 55 bis 86
// - Es werden nicht mehr nur GAST_-Rollen angezeigt
// - SUPERPARTNER, PARTNER, TECHNIKER und PRESSE werden ergänzt
// - Falls /roles einzelne Rollen nicht liefert, werden sie als
//   Fallback trotzdem anhand der bekannten IDs angezeigt
// ============================================================

import React, { useState, useEffect, useMemo } from "react";
import Api from "../../Api";
import Toast from "../Toast";

import "../../styles/guestRegistration.css";

const MAX_DAYS = 2;

// ============================================================
// ERLAUBTE ROLLEN FÜR DIE GÄSTE-REGISTRATION
//
// Wichtig:
// - Diese IDs entsprechen deiner SQL-Tabelle role.
// - Nur diese Rollen sollen im Dropdown erscheinen.
// - Die Reihenfolge hier bestimmt auch die Reihenfolge im Select.
// ============================================================
const GUEST_REGISTRATION_ROLES = [
	{ roleId: 55, name: "SUPERPARTNER", label: "Superpartner" },
	{ roleId: 56, name: "PARTNER", label: "Partner" },
	{ roleId: 80, name: "GAST_OK", label: "Gast-OK" },
	{ roleId: 81, name: "GAST_ANWOHNER", label: "Gast-Anwohner" },
	{ roleId: 82, name: "GAST_BAND", label: "Gast-Band" },
	{ roleId: 83, name: "GAST_PETZI", label: "Gast-Petzi" },
	{ roleId: 84, name: "GAST_FESTIVAL", label: "Gast-Festival" },
	{ roleId: 85, name: "TECHNIKER", label: "Techniker" },
	{ roleId: 86, name: "PRESSE", label: "Presse" }
];

const ALLOWED_GUEST_ROLE_IDS = GUEST_REGISTRATION_ROLES.map((role) => role.roleId);
const ALLOWED_GUEST_ROLE_NAMES = GUEST_REGISTRATION_ROLES.map((role) => role.name);

// ============================================================
// Helper: Text vereinheitlichen
// ============================================================
const normalizeText = (value) => {
	return String(value || "").trim();
};

const normalizeUpper = (value) => {
	return normalizeText(value).toUpperCase();
};

// ============================================================
// Helper: Rollen-ID robust lesen
//
// Zweck:
// - Je nach Backend-DTO kann die ID roleId, role_id oder id heissen.
// ============================================================
const getRoleId = (roleItem) => {
	const rawId =
		roleItem?.roleId ??
		roleItem?.role_id ??
		roleItem?.id ??
		null;

	if (rawId === null || rawId === undefined || rawId === "") {
		return null;
	}

	const numberValue = Number(rawId);

	return Number.isNaN(numberValue) ? null : numberValue;
};

// ============================================================
// Helper: Rollenname robust lesen
//
// Zweck:
// - Je nach Backend-DTO kann der Rollenname name, role,
//   roleName, role_name oder authority heissen.
// ============================================================
const getRoleName = (roleItem) => {
	return normalizeUpper(
		roleItem?.name ||
		roleItem?.role ||
		roleItem?.roleName ||
		roleItem?.role_name ||
		roleItem?.authority ||
		""
	);
};

// ============================================================
// Helper: Anzeige-Label für eine Rolle holen
// ============================================================
const getRoleLabel = (roleItem) => {
	const roleId = getRoleId(roleItem);
	const roleName = getRoleName(roleItem);

	const configuredRole = GUEST_REGISTRATION_ROLES.find((role) => {
		return role.roleId === roleId || role.name === roleName;
	});

	if (configuredRole) {
		return configuredRole.label;
	}

	return roleName
		.replace("GAST_", "Gast-")
		.replaceAll("_", " ");
};

// ============================================================
// Helper: Prüfen, ob Rolle für Gäste erlaubt ist
//
// Wichtig:
// - Es wird nach ID UND Name geprüft.
// - Dadurch funktioniert es auch, wenn das Backend eine ID oder
//   einen Namen anders strukturiert liefert.
// ============================================================
const isAllowedGuestRole = (roleItem) => {
	const roleId = getRoleId(roleItem);
	const roleName = getRoleName(roleItem);

	return (
		ALLOWED_GUEST_ROLE_IDS.includes(roleId) ||
		ALLOWED_GUEST_ROLE_NAMES.includes(roleName)
	);
};

// ============================================================
// Helper: Rollen korrekt sortieren
// ============================================================
const sortGuestRoles = (a, b) => {
	const roleIdA = getRoleId(a);
	const roleIdB = getRoleId(b);

	const indexA = ALLOWED_GUEST_ROLE_IDS.indexOf(roleIdA);
	const indexB = ALLOWED_GUEST_ROLE_IDS.indexOf(roleIdB);

	const safeIndexA = indexA === -1 ? 999 : indexA;
	const safeIndexB = indexB === -1 ? 999 : indexB;

	return safeIndexA - safeIndexB;
};

// ============================================================
// Helper: Fehlende Rollen aus Fallback ergänzen
//
// Zweck:
// - Falls /roles z. B. nur GAST_-Rollen liefert,
//   werden SUPERPARTNER, PARTNER, TECHNIKER und PRESSE trotzdem
//   im Dropdown angezeigt.
// - Voraussetzung: Diese IDs existieren in der Datenbank.
// ============================================================
const mergeApiRolesWithFallbackRoles = (apiRoles) => {
	const mergedRoles = [];

	GUEST_REGISTRATION_ROLES.forEach((configuredRole) => {
		const apiRole = apiRoles.find((roleItem) => {
			const roleId = getRoleId(roleItem);
			const roleName = getRoleName(roleItem);

			return (
				roleId === configuredRole.roleId ||
				roleName === configuredRole.name
			);
		});

		if (apiRole) {
			mergedRoles.push({
				...apiRole,
				roleId: getRoleId(apiRole) ?? configuredRole.roleId,
				name: getRoleName(apiRole) || configuredRole.name
			});
			return;
		}

		// Fallback, falls der Endpoint die Rolle nicht zurückliefert.
		mergedRoles.push({
			roleId: configuredRole.roleId,
			name: configuredRole.name
		});
	});

	return mergedRoles;
};

// ============================================================
// Helper: Eintritte in Wert umrechnen
//
// Freitag/Samstag = 1 Tag
// Festivalpass = 2 Tage
// ============================================================
const getAdmissionValue = (admission) => {
	if (!admission) {
		return 0;
	}

	const value = admission.toLowerCase();

	if (value === "festivalpass") {
		return 2;
	}

	if (value === "freitag") {
		return 1;
	}

	if (value === "samstag") {
		return 1;
	}

	return 0;
};

// ============================================================
// Helper: Backend-Fehlermeldung robust auslesen
// ============================================================
const extractBackendMessage = (err) => {
	const data = err?.response?.data;

	if (!data) {
		return null;
	}

	if (typeof data === "string") {
		return data;
	}

	if (typeof data === "object") {
		return (
			data.message ||
			data.error ||
			data?.errors?.message ||
			null
		);
	}

	return null;
};

const GuestRegistrationDesktop = ({
	refreshGuestList,
	guests = [],
	guestAccessMode = "",
	forceAdminGuestRules = false,
	forceRestrictedGuestRules = false
}) => {

	// ============================================================
	// USER CONTEXT
	// ============================================================
	const department = localStorage.getItem("department")?.toLowerCase();
	const role = localStorage.getItem("role");

	const recordedById = localStorage.getItem("volunteerId");

	// ============================================================
	// BERECHTIGUNG
	//
	// restricted:
	// - normale Ressort-Gästeliste
	// - maximal 2 Eintrittstage
	//
	// admin:
	// - Adminansicht
	// - unbegrenzt
	// - Rollenauswahl sichtbar
	// ============================================================
	const isForcedAdmin =
		guestAccessMode === "admin" ||
		forceAdminGuestRules === true;

	const isForcedRestricted =
		guestAccessMode === "restricted" ||
		forceRestrictedGuestRules === true;

	const isKasseOK = role === "OK" && department === "kasse";
	const isAdmin = role === "ADMIN" || role === "ROLE_ADMIN";

	const canRegisterUnlimitedGuests =
		isForcedAdmin ||
		(!isForcedRestricted && (isAdmin || isKasseOK));

	const canSelectGuestRole =
		isForcedAdmin ||
		(!isForcedRestricted && (isAdmin || isKasseOK));

	// ============================================================
	// STATE
	// ============================================================
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [admissionId, setAdmissionId] = useState("");
	const [roleId, setRoleId] = useState("");

	const [roles, setRoles] = useState([]);
	const [admissions, setAdmissions] = useState([]);

	const [toast, setToast] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	// ============================================================
	// LOAD DATA
	// ============================================================
	useEffect(() => {
		loadRoles();
		loadAdmissions();
	}, []);

	// ============================================================
	// Rollen laden
	//
	// Wichtig:
	// - Es werden exakt die Rollen 55 bis 86 angezeigt.
	// - Falls der Backend-Endpoint einzelne Rollen nicht zurückgibt,
	//   werden sie aus GUEST_REGISTRATION_ROLES ergänzt.
	// ============================================================
	const loadRoles = async () => {
		try {
			const res = await Api.get("/roles");
			const apiRoles = Array.isArray(res.data) ? res.data : [];

			const allowedApiRoles = apiRoles.filter(isAllowedGuestRole);
			const mergedRoles = mergeApiRolesWithFallbackRoles(allowedApiRoles);

			setRoles(mergedRoles.sort(sortGuestRoles));
		} catch (error) {
			console.error("❌ Fehler beim Laden der Rollen:", error);

			// Falls /roles komplett fehlschlägt, werden die bekannten Rollen
			// trotzdem angezeigt. Die IDs müssen dafür in der Datenbank existieren.
			setRoles(GUEST_REGISTRATION_ROLES);
		}
	};

	// ============================================================
	// Eintritte laden
	//
	// "Kein Eintritt" wird bewusst nicht angezeigt.
	// ============================================================
	const loadAdmissions = async () => {
		try {
			const res = await Api.get("/admissions");
			const apiAdmissions = Array.isArray(res.data) ? res.data : [];

			const filteredAdmissions = apiAdmissions.filter((admissionItem) => {
				const value = admissionItem.admission?.toLowerCase().trim();

				return value && !value.includes("kein");
			});

			setAdmissions(filteredAdmissions);
		} catch (error) {
			console.error("❌ Fehler beim Laden der Eintritte:", error);
			setAdmissions([]);
		}
	};

	// ============================================================
	// MEINE GÄSTE
	//
	// Wird für die 2-Tage-Regel benutzt.
	// ============================================================
	const myGuests = useMemo(() => {
		if (!recordedById) {
			return [];
		}

		return guests.filter((guest) =>
			String(guest.recordedById) === String(recordedById)
		);
	}, [guests, recordedById]);

	// ============================================================
	// EINTRITTSTAGE BERECHNEN
	// ============================================================
	const currentDays = useMemo(() => {
		return myGuests.reduce((sum, guest) => {
			return sum + getAdmissionValue(guest.admission);
		}, 0);
	}, [myGuests]);

	const remainingSlots = Math.max(0, MAX_DAYS - currentDays);

	// ============================================================
	// SAVE
	// ============================================================
	const handleSaveGuest = async () => {
		if (isLoading) {
			return;
		}

		if (!firstName || !lastName || !admissionId) {
			setToast({
				type: "error",
				message: "Bitte Vorname, Nachname und Eintritt ausfüllen."
			});
			return;
		}

		if (canSelectGuestRole && !roleId) {
			setToast({
				type: "error",
				message: "Bitte eine Rolle auswählen."
			});
			return;
		}

		if (!recordedById) {
			setToast({
				type: "error",
				message: "Der Erfasser konnte nicht ermittelt werden."
			});
			return;
		}

		const selectedAdmission = admissions.find(
			(admissionItem) =>
				Number(admissionItem.admissionId) === Number(admissionId)
		);

		const newValue = getAdmissionValue(selectedAdmission?.admission);

		// ========================================================
		// LIMIT NUR BEI EINGESCHRÄNKTER NORMALANSICHT
		// ========================================================
		if (!canRegisterUnlimitedGuests && currentDays + newValue > MAX_DAYS) {
			setToast({
				type: "error",
				message: "Maximal 2 Eintrittstage erlaubt."
			});
			return;
		}

		try {
			setIsLoading(true);

			const requestBody = {
				firstName,
				lastName,
				admissionId: Number(admissionId),
				recordedById: Number(recordedById)
			};

			// Rolle nur mitsenden, wenn eine Rollenwahl erlaubt ist.
			// In der normalen eingeschränkten Kasse-Gästeliste bleibt
			// die Rollenwahl verborgen.
			if (canSelectGuestRole) {
				requestBody.roleId = Number(roleId);
			}

			await Api.post("/guests", requestBody);

			setToast({
				type: "success",
				message: "Gast erfolgreich registriert 🎉"
			});

			setFirstName("");
			setLastName("");
			setAdmissionId("");
			setRoleId("");

			if (refreshGuestList) {
				await refreshGuestList();
			}

		} catch (err) {
			const status = err?.response?.status;
			const backendMessage = extractBackendMessage(err);

			if (status === 409) {
				setToast({
					type: "warning",
					message: backendMessage || "Dieser Gast ist bereits eingeschrieben."
				});
				return;
			}

			if (backendMessage?.includes("Maximal")) {
				setToast({
					type: "error",
					message: backendMessage
				});
				return;
			}

			setToast({
				type: "error",
				message: backendMessage || "Fehler beim Registrieren."
			});

		} finally {
			setIsLoading(false);
		}
	};

	// ============================================================
	// RENDER
	// ============================================================
	return (
		<>
			{toast && (
				<Toast
					{...toast}
					onClose={() => setToast(null)}
				/>
			)}

			<div className="guest-list-container guest-registration-container">
				<h3 className="guest-list-title">
					Gast einschreiben
				</h3>

				{/* Counter nur, wenn keine unbegrenzten Gäste erlaubt sind */}
				{!canRegisterUnlimitedGuests && (
					<div className="guest-slots-info">
						Noch freie Eintrittstage:{" "}
						<strong>{remainingSlots}</strong> / {MAX_DAYS}
					</div>
				)}

				<div className="guest-form">
					<input
						placeholder="Vorname"
						value={firstName}
						onChange={(event) => setFirstName(event.target.value)}
					/>

					<input
						placeholder="Nachname"
						value={lastName}
						onChange={(event) => setLastName(event.target.value)}
					/>

					<select
						value={admissionId}
						onChange={(event) => setAdmissionId(event.target.value)}
					>
						<option value="">
							Eintritt wählen
						</option>

						{admissions.map((admissionItem) => (
							<option
								key={admissionItem.admissionId}
								value={admissionItem.admissionId}
							>
								{admissionItem.admission}
							</option>
						))}
					</select>

					{/* ====================================================
					    Rollenauswahl
					    Wird nur bei Admin/Kasse-Adminansicht angezeigt.
					   ==================================================== */}
					{canSelectGuestRole && (
						<select
							value={roleId}
							onChange={(event) => setRoleId(event.target.value)}
						>
							<option value="">
								Rolle wählen
							</option>

							{roles.map((roleItem) => {
								const currentRoleId = getRoleId(roleItem);

								return (
									<option
										key={currentRoleId}
										value={currentRoleId}
									>
										{getRoleLabel(roleItem)}
									</option>
								);
							})}
						</select>
					)}

					<button
						type="button"
						onClick={handleSaveGuest}
						disabled={isLoading}
					>
						{isLoading ? "Speichern..." : "Gast erfassen"}
					</button>
				</div>
			</div>
		</>
	);
};

export default GuestRegistrationDesktop;